import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useEffect, useState } from 'react';
import {
  DollarSign, Coins, TrendingUp, TrendingDown, Users, AlertTriangle,
  CheckCircle2, XCircle,
} from 'lucide-react';
import {
  getAiSpendReport, getAiSpendOrphans, getAiUsageSummary,
  withMargin, sumPivot,
  type AiSpendOrphans, type AiUsageAlert, type PivotTotals,
} from '../../../services/aiControlApi';
import { getRubyLedgerWindow, type LedgerTotals } from '../../../services/aiControl/ledger';
import { tokensLabel } from '../rubyAi/units';
import { thumbsUpRate, tenantMarginsFromLedger } from '../ledger/ledgerModel';
import type { AiTabProps } from '../AiControlCenter';
import { RubyLoader } from '../../../components/RubyLoader';

const usd = (n: number): string =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

/** One tenant in the margin lists — charged / vendor cost / profit from the Ruby Ledger. */
interface TenantMarginRow { key: string; label: string; revenueUsd: number; costUsd: number; marginUsd: number }

const OverviewTab: React.FC<AiTabProps> = ({
  registry, registryError, range, onHeaderStats,
}) => {
  const [totals, setTotals] = useState<PivotTotals | null>(null);
  const [topTenants, setTopTenants] = useState<TenantMarginRow[]>([]);
  const [worstTenants, setWorstTenants] = useState<TenantMarginRow[]>([]);
  const [orphans, setOrphans] = useState<AiSpendOrphans | null>(null);
  const [alerts, setAlerts] = useState<AiUsageAlert[]>([]);
  const [ledgerTotals, setLedgerTotals] = useState<LedgerTotals | null>(null);
  const [ledgerPartial, setLedgerPartial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setLedgerTotals(null);
      setLedgerPartial(false);
      // Vendor spend comes from the spend pivot (every call, incl. admin tests
      // and platform jobs). What tenants were CHARGED and the margin come from
      // the Ruby Ledger (RUN rows): engine spend rows carry creditsCharged 0,
      // so the pivot's "revenue" is not a charge figure (final review #3).
      const [reportRes, ledgerRes] = await Promise.allSettled([
        getAiSpendReport('tenant', range),
        getRubyLedgerWindow({ fromIso: range.fromIso, toIso: range.toIso }),
      ]);
      if (cancelled) return;
      let spendCost = 0;
      const labels = new Map<string, string>();
      if (reportRes.status === 'fulfilled') {
        const rows = withMargin(reportRes.value.rows, reportRes.value.creditUnitPriceUsd);
        const t = sumPivot(rows);
        setTotals(t);
        spendCost = t.costUsd;
        for (const r of rows) labels.set(r.key, r.label);
      } else {
        setError((reportRes.reason as any)?.message || 'Failed to load the spend report');
      }
      let lt: LedgerTotals | null = null;
      let partial = false;
      if (ledgerRes.status === 'fulfilled') {
        lt = ledgerRes.value.totals;
        partial = ledgerRes.value.partial;
        setLedgerTotals(lt);
        setLedgerPartial(partial);
        const margins: TenantMarginRow[] = tenantMarginsFromLedger(ledgerRes.value.items).map((m) => ({
          key: m.tenantId, label: labels.get(m.tenantId) || m.tenantId,
          revenueUsd: m.chargedUsd, costUsd: m.vendorCostUsd, marginUsd: m.profitUsd,
        }));
        setTopTenants([...margins].sort((a, b) => b.marginUsd - a.marginUsd).slice(0, 5));
        setWorstTenants(margins.filter((r) => r.marginUsd < 0).sort((a, b) => a.marginUsd - b.marginUsd).slice(0, 5));
      } else {
        setTopTenants([]);
        setWorstTenants([]);
      }
      const mark = partial ? ' (partial)' : '';
      onHeaderStats([
        { label: 'Spend',   value: usd(spendCost), tone: 'rose' },
        { label: 'Charged', value: lt ? `${usd(lt.chargedUsd)}${mark}` : '—', tone: 'blue' },
        { label: 'Margin',  value: lt ? `${usd(lt.profitUsd)}${mark}` : '—', tone: !lt || lt.profitUsd >= 0 ? 'emerald' : 'rose' },
        { label: 'Tenants', value: String(labels.size), tone: 'white' },
        { label: 'Alerts',  value: '0', tone: 'amber' },
      ]);
      setLoading(false);

      // Orphans and alerts are independent — one failing must not blank the page.
      try {
        const o = await getAiSpendOrphans();
        if (!cancelled) setOrphans(o);
      } catch (_) { /* sweeper endpoint may not be deployed yet */ }
      try {
        const summary = await getAiUsageSummary(range);
        if (!cancelled) setAlerts(summary.alerts || []);
      } catch (_) { /* legacy summary is best-effort here */ }
    };
    void load();
    return () => { cancelled = true; };
  }, [range.fromIso, range.toIso, onHeaderStats]);

  const enabledProviders = (registry?.providers || []).filter((p) => p.enabled);
  const disabledProviders = (registry?.providers || []).filter((p) => !p.enabled);

  return (
    <>
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Failed to load AI spend</p>
            <p className="text-rose-600 text-xs mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Money strip. Margin is the biggest number on the page — it is the
          figure the whole design exists to produce (spec §4). Charged and
          margin are the Ruby Ledger's (RUN rows); provider cost is every vendor
          call from the spend pivot. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <MoneyCard
          icon={<DollarSign size={16} />} label="Provider cost"
          value={usd(totals?.costUsd ?? 0)} tone="rose" loading={loading}
          hint="Every vendor call, incl. admin tests"
        />
        <MoneyCard
          icon={<Coins size={16} />} label="Charged to tenants"
          value={ledgerTotals ? usd(ledgerTotals.chargedUsd) : '—'} tone="blue" loading={loading}
          hint={ledgerTotals ? `${tokensLabel(ledgerTotals.tokensCharged)}${ledgerPartial ? ' · partial' : ''}` : 'Ruby Ledger'}
        />
        <MoneyCard
          icon={(ledgerTotals?.profitUsd ?? 0) >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          label="Margin"
          value={ledgerTotals ? usd(ledgerTotals.profitUsd) : '—'}
          tone={(ledgerTotals?.profitUsd ?? 0) >= 0 ? 'emerald' : 'rose'}
          loading={loading}
          hint={ledgerTotals
            ? `${ledgerTotals.chargedUsd > 0 ? ((ledgerTotals.profitUsd / ledgerTotals.chargedUsd) * 100).toFixed(1) : '0.0'}% of charged · Ruby Ledger${ledgerPartial ? ' · partial' : ''}`
            : 'Ruby Ledger unavailable'}
          emphasis
        />
        <MoneyCard
          icon={<Users size={16} />} label="Tokens"
          value={(totals?.tokens ?? 0).toLocaleString()} tone="stone" loading={loading}
          hint={`${(totals?.requests ?? 0).toLocaleString()} calls`}
        />
      </div>

      {ledgerPartial && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-2.5 text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Ruby Ledger totals are <strong>partial</strong>: this window has more runs than the
            Overview reads. Narrow the range, or page through the Ledger tab for the full figure.
          </span>
        </div>
      )}

      {/* Ruby Ledger totals for this window (Task 17) — summed across every
          ledger page (cursor), flagged partial when the page budget runs out.
          Degrades to a dash, never a blank row, when the ledger endpoint fails. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <MoneyCard
          icon={<Coins size={16} />} label="Charged (Ruby Tokens)"
          value={ledgerTotals ? tokensLabel(ledgerTotals.tokensCharged) : '—'} tone="blue" loading={loading}
          hint="Ruby Ledger"
        />
        <MoneyCard
          icon={<DollarSign size={16} />} label="Vendor cost"
          value={ledgerTotals ? usd(ledgerTotals.vendorCostUsd) : '—'} tone="rose" loading={loading}
          hint="Ruby Ledger"
        />
        <MoneyCard
          icon={<AlertTriangle size={16} />} label="Refunded runs"
          value={ledgerTotals ? String(ledgerTotals.refundedRuns) : '—'} tone="stone" loading={loading}
          hint={`of ${ledgerTotals?.runs ?? 0} runs`}
        />
        <MoneyCard
          icon={<CheckCircle2 size={16} />} label="👍 rate"
          value={ledgerTotals ? thumbsUpRate(ledgerTotals) : '—'} tone="stone" loading={loading}
          hint={ledgerTotals ? `${ledgerTotals.good} 👍 · ${ledgerTotals.bad} 👎` : 'Ruby Ledger'}
        />
      </div>

      {/* Unreconciled spend — the ONLY control that detects money already
          lost (spec M2). An orphan is evidence, never something to clear. */}
      {orphans && orphans.count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {orphans.count} unreconciled reservation{orphans.count === 1 ? '' : 's'}
                {' · '}~{usd(orphans.estimatedUnrecordedUsd)} possibly unrecorded
              </p>
              <p className="text-[11px] text-gray-600 mt-0.5">
                Reserved with no commit and no refund past the sweep window. The vendor
                very likely billed us and the ledger never learned. Oldest:{' '}
                {Math.round(orphans.oldestAgeMs / 60000)}m. These are flagged, never
                auto-cleared — clearing one erases the evidence.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MarginList
          title="Top margin — tenants earning the most"
          rows={topTenants}
          empty="No Ruby runs in the selected window."
          loading={loading}
        />
        <MarginList
          title="Negative margin — tenants costing more than they pay"
          rows={worstTenants}
          empty="No tenant is running at a loss in this window."
          loading={loading}
          negative
        />
      </div>

      {/* Provider health from the registry, not from a hardcoded card list. */}
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <h2 className="text-sm font-semibold text-gray-900">Provider health</h2>
          <p className="text-[11px] text-gray-500">
            Live from the registry. Editing happens on Providers &amp; Models.
          </p>
        </div>
        {registryError ? (
          <div className="p-6 text-sm text-rose-700">{registryError}</div>
        ) : !registry ? (
          <RubyLoader variant="section" />
        ) : registry.providers.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No providers registered yet. Plan 3's migration script populates them.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {[...enabledProviders, ...disabledProviders].map((p) => {
              const models = registry.models.filter((m) => m.providerId === p.providerId);
              const live = models.filter((m) => m.enabled).length;
              return (
                <li key={p.providerId} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {p.enabled
                      ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                      : <XCircle size={16} className="text-stone-300 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.label}</p>
                      <p className="text-[11px] text-gray-500 font-mono truncate">
                        {p.vendor} · {live}/{models.length} model{models.length === 1 ? '' : 's'} enabled
                      </p>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-medium ${
                    p.enabled ? 'text-emerald-700 bg-emerald-100' : 'text-stone-600 bg-stone-100'
                  }`}>
                    {p.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <h2 className="text-sm font-semibold text-gray-900">Alerts</h2>
        </div>
        {alerts.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No alerts raised.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {alerts.map((a) => (
              <li key={a.sk} className="px-4 py-3 flex items-start gap-3">
                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{a.message || a.type || 'alert'}</p>
                  <p className="text-[11px] text-gray-400">{a.checkedAt || a.sk}</p>
                </div>
                <span className="text-[11px] uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                  {a.severity}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

const TONE_CLASSES: Record<string, string> = {
  rose: 'bg-rose-100 text-rose-600',
  blue: 'bg-blue-100 text-blue-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  stone: 'bg-stone-100 text-stone-600',
};

const MoneyCard: React.FC<{
  icon: React.ReactNode; label: string; value: string; hint: string;
  tone: 'rose' | 'blue' | 'emerald' | 'stone'; loading?: boolean; emphasis?: boolean;
}> = ({ icon, label, value, hint, tone, loading, emphasis }) => (
  <div className={`bg-white border rounded-xl p-4 shadow-sm ${
    emphasis ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-stone-200'
  }`}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</p>
        <p className={`font-semibold text-gray-900 mt-1 ${emphasis ? 'text-3xl' : 'text-2xl'}`}>
          {loading ? <RubyGemMark size={emphasis ? 30 : 24} drawing title="Loading" /> : value}
        </p>
        <p className="text-[11px] text-gray-400 mt-0.5 truncate">{hint}</p>
      </div>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${TONE_CLASSES[tone]}`}>
        {icon}
      </div>
    </div>
  </div>
);

const MarginList: React.FC<{
  title: string; rows: TenantMarginRow[]; empty: string; loading: boolean; negative?: boolean;
}> = ({ title, rows, empty, loading, negative }) => (
  <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-stone-100">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    </div>
    {loading ? (
      <RubyLoader variant="section" />
    ) : rows.length === 0 ? (
      <div className="p-6 text-center text-sm text-gray-500">{empty}</div>
    ) : (
      <ul className="divide-y divide-stone-100">
        {rows.map((r) => (
          <li key={r.key} className="px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-900 truncate">{r.label}</p>
              <p className="text-[11px] text-gray-400">
                {usd(r.revenueUsd)} charged · {usd(r.costUsd)} cost
              </p>
            </div>
            <span className={`text-sm font-semibold whitespace-nowrap ${
              negative || r.marginUsd < 0 ? 'text-rose-600' : 'text-emerald-700'
            }`}>
              {usd(r.marginUsd)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default OverviewTab;
